import { concatMap, debounceTime, finalize, take, tap } from 'rxjs/operators';
import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { TripService } from '../shared/trip.service';
import { ActivatedRoute, Params } from '@angular/router';
import { Trip } from '../shared/trip.model';
import { Subject, Subscription } from 'rxjs';
import { AngularFireDatabase } from '@angular/fire/database';
import { HttpErrorResponse } from '@angular/common/http';
import { ToastrService } from 'ngx-toastr';
import { LoaderService } from '../../shared/services/loader.service';
import { DistanceUnit } from 'app/shared/distance-unit';
import { TripManagementService } from '../shared/trip-management-service';
import { MapManagementService } from '../shared/map-management-service';
import { LoadTrackingMapComponent } from '../load-tracking-monitoring/load-tracking-map/load-tracking-map.component';
import LatLngLiteral = google.maps.LatLngLiteral;
import Map = google.maps.Map;
import { DateTimezoneService } from '../shared/date-timezone.service';

@Component({
  selector: 'app-track-a-trip-integration',
  templateUrl: './track-a-trip-integration.component.html',
  styleUrls: ['./track-a-trip-integration.component.scss']
})

export class TrackATripIntegrationComponent implements OnInit, OnDestroy {

  trip: Trip;

  map: any;
  _showForecastPath = false;
  forecastPathReady = false;

  DistanceUnit = DistanceUnit;
  _distanceUnit = DistanceUnit.KILOMETERS;

  lat  = this.mapManagementService.defaultLat;
  lng  = this.mapManagementService.defaultLng;
  zoom = this.mapManagementService.defaultZoom;

  latLngLiteralSubject = new Subject<LatLngLiteral>();
  zoomSubject          = new Subject<number>();

  positionSubscription: Subscription;
  latLngLiteralSubscription: Subscription;
  zoomSubscription: Subscription;
  ready = true;
  refreshing = false;

  @ViewChild('loadTrackingMap', { static: true })
  loadTrackingMapComponent: LoadTrackingMapComponent;
  get showForecastPath(): boolean {
    return this._showForecastPath;
  }

  set showForecastPath(value: boolean) {
    this._showForecastPath = value;

    if (value) {
      this.changeShowForecastPath();
    }
  }

  get distanceUnit(): DistanceUnit {
    return this._distanceUnit;
  }
  set distanceUnit(value: DistanceUnit) {
    this._distanceUnit = value;

    if (value) {
      this.distanceUnitChanged(value);
    }
  }

  constructor(
    private route: ActivatedRoute,
    private tripService: TripService,
    private mapManagementService: MapManagementService,
    private dateTimezoneService: DateTimezoneService,
    private af: AngularFireDatabase,
    private toastr: ToastrService,
    private loader: LoaderService,
    private tripManagementService: TripManagementService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit() {
    this.route.params.subscribe((params: Params) => {
      this.route.queryParams
        .pipe(take(1))
        .subscribe(queryParams => {
          this.loader.startLoad(false);
          const deliveryNumber: string = queryParams.deliveryNumber;
          const hash: string = queryParams.hash;
          const service: string = params.serviceName;

          this.tripService.searchByDeliveryNumber(deliveryNumber, hash, service)
            .pipe(finalize(() => this.loader.stopLoad()))
            .subscribe((trip) => {
              if (trip) {
                this.trip = trip;

                this.dateTimezoneService.timezoneSourceChange(true, trip.timezone);
                this.watchTripPosition(trip);
                this.initTrip();
              } else {
                this.toastr.error(`Cannot find any trip with delivery number: ${deliveryNumber}`, 'Error');
              }

            }, (error: HttpErrorResponse) => {
              console.log(error);
              this.toastr.error((error && error.error && error.error.message) ? error.error.message : 'The selected delivery number does not have a trip registered in CMA', 'Error');
            });
        });
    });

    this.latLngLiteralSubscription = this.latLngLiteralSubject.pipe(
      debounceTime(1000)
    ).subscribe((latLngLiteral: LatLngLiteral) => {
      this.lat = latLngLiteral.lat;
      this.lng = latLngLiteral.lng;
      this.cdr.detectChanges();
    });

    this.zoomSubscription = this.zoomSubject.pipe(
      debounceTime(1000)
    ).subscribe(zoom => {
      this.zoom = zoom;
      this.cdr.detectChanges();
    });
  }

  initTrip() {
    const positionsObservable = this.tripManagementService.initTrip(this.trip, true);

    this.loader.startLoadAndCloseWhenComplete(positionsObservable.pipe(
      tap(([origin, destination]) => {
        this.trip.originPosition = origin;
        this.trip.destPosition   = destination;
      }),
      concatMap(() => this.tripManagementService.refreshTripDetails(
        this.trip,
        true,
        false,
        false,
        true,
        false,
        this.showForecastPath,
        null,
        null,
        this.distanceUnit
      )
        .observable),
    ))
      .subscribe(() => {
        if (this.trip && this.trip.latestPing) {
          this.lat = this.trip.latestPing.latitude;
          this.lng = this.trip.latestPing.longitude;
        }
        if (this.trip) {
          this.trip.timelineParamsInitialized = true;
          setTimeout(() => this.centerView(), 1000);
        }
        this.cdr.detectChanges();
      });
  }

  forceRefreshTripDetails(trip: Trip) {
    this.refreshing = true;
    this.loader.startLoadAndCloseWhenComplete(
      this.tripManagementService.refreshTripDetails(
        trip,
        true,
        false,
        true,
        false,
        false,
        this.showForecastPath,
        null,
        null,
        this.distanceUnit
      )
        .observable
    )
      .subscribe(() => {
        this.refreshing = false;
        this.cdr.detectChanges();
      });
  }

  watchTripPosition(trip: Trip) {
    this.positionSubscription = this.tripManagementService.getTripPositionObservable(this.trip)
      .subscribe(ping => {
        trip.latestPing = ping;
        this.cdr.detectChanges();
      });
  }

  resetSubscriptions() {
    if (this.positionSubscription) {
      this.positionSubscription.unsubscribe();
    }
  }

  changeShowForecastPath() {
    if (this.showForecastPath && this.trip) {
      this.loader.startLoadAndCloseWhenComplete(
        this.tripManagementService.refreshTripForecastPath(this.trip, true, this.distanceUnit, true)
      ).subscribe(() => {
        this.forecastPathReady = true;
        this.cdr.detectChanges();
      });
    }
  }

  distanceUnitChanged(distanceUnit: DistanceUnit) {
    if (this.tripManagementService.metricsDistance.has(this.trip.tripId) &&
      this.tripManagementService.imperialDistance.has(this.trip.tripId)) {
      let distance
      if (distanceUnit === DistanceUnit.MILES) {
        distance = this.tripManagementService.imperialDistance.get(this.trip.tripId);
      } else {
        distance = this.tripManagementService.metricsDistance.get(this.trip.tripId);
      }
      this.trip.distanceToArrival = distance;
      this.cdr.detectChanges();
    }
  }

  onMapReady(map: Map) {
    this.map = map;
  }

  centerView() {
    this.ready = false;
    this.mapManagementService.centerView(this.trip, this.map, true);
    this.ready = true;
  }

  ngOnDestroy() {
    this.resetSubscriptions();
  }

}
